import React, { useEffect, useState } from 'react';
import { getPricingConfig, normalizePricingConfig, savePricingConfig, ColorKey } from '../../utils/pricingConfig';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Users from './Users';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';
type PreviewElement = 'headerBg' | 'companyName' | 'companySubtitle' | 'badge' | 'contact' | 'sectionTitle' | 'validity' | 'body' | 'total' | 'legal';

type ColorSwatchPickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

const ColorSwatchPicker: React.FC<ColorSwatchPickerProps> = ({ label, value, onChange }) => (
  <div>
    <label className="text-sm font-medium block mb-2">{label}</label>
    <div className="flex items-center gap-3">
      <label
        className="w-10 h-10 rounded-full border border-gray-300 cursor-pointer inline-block"
        style={{ backgroundColor: value }}
        title={value}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
      </label>
      <span className="text-sm text-gray-600">{value}</span>
    </div>
  </div>
);

const Settings: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'administrador' || user?.role === 'superadmin';
  const initial = getPricingConfig();
  const [valorDolar, setValorDolar] = useState(initial.valorDolar);
  const [preciosPorColor, setPreciosPorColor] = useState(initial.preciosPorColor);
  const [pdfTemplate, setPdfTemplate] = useState(initial.pdfTemplate);
  const [message, setMessage] = useState('');
  const [activeSubmodule, setActiveSubmodule] = useState<'general' | 'pdf' | 'usuarios'>('general');
  const [selectedPreviewElement, setSelectedPreviewElement] = useState<PreviewElement>('companyName');
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  useBodyScrollLock(isEditorModalOpen);
  useEscapeToClose(isEditorModalOpen, () => setIsEditorModalOpen(false));
  const [isFetchingRate, setIsFetchingRate] = useState(false);
  const [isGeneratingRealPreview, setIsGeneratingRealPreview] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [realPreviewUrl, setRealPreviewUrl] = useState('');
  const [lastRates, setLastRates] = useState<{ buy: number; sell: number } | null>(null);
  const addLegalLine = () => setPdfTemplate((p) => ({ ...p, legalLines: [...p.legalLines, ''] }));
  const removeLegalLine = (index: number) => setPdfTemplate((p) => ({ ...p, legalLines: p.legalLines.filter((_, i) => i !== index) }));
  const updateLegalLine = (index: number, value: string) => setPdfTemplate((p) => ({ ...p, legalLines: p.legalLines.map((line, i) => i === index ? value : line) }));
  const openEditorFor = (element: PreviewElement) => {
    setSelectedPreviewElement(element);
    setIsEditorModalOpen(true);
  };

  const onSave = async () => {
    const nextConfig = { valorDolar, preciosPorColor, pdfTemplate };
    savePricingConfig(nextConfig);
    try {
      await apiService.savePricingConfig(nextConfig);
      setMessage('Configuracion guardada en base de datos.');
    } catch {
      setMessage('Se guardo localmente, pero fallo guardar en base de datos.');
    }
    window.setTimeout(() => setMessage(''), 2500);
  };

  const onFetchDollarRate = async () => {
    setIsFetchingRate(true);
    try {
      const response = await apiService.getCurrencyRates();
      const buy = Number(response?.rates?.buy || 0);
      const sell = Number(response?.rates?.sell || 0);
      setLastRates((buy > 0 || sell > 0) ? { buy, sell } : null);
      const rate = sell > 0 ? sell : 0;
      if (response?.success && !response?.fallback && rate > 0) {
        setValorDolar(rate);
        setMessage(`Dolar venta actualizado desde ${response.source}: ${rate.toFixed(2)} ARS`);
      } else {
        setMessage('No se pudo obtener cotizacion real. No se actualizo el valor.');
      }
    } catch {
      setMessage('No se pudo traer el dolar actual desde la API.');
    } finally {
      setIsFetchingRate(false);
      window.setTimeout(() => setMessage(''), 3000);
    }
  };

  const generateRealPdfPreview = async () => {
    try {
      setIsGeneratingRealPreview(true);
      const { blob } = await apiService.generateQuotePdf({
        quoteNumber: 'PREVIEW-000',
        registerQuote: false,
        customerName: 'Appteck',
        customerEmail: 'cliente@ejemplo.com',
        customerCompany: 'Appteck',
        anchoCm: 45,
        largoCm: 60,
        micrones: 23,
        bolsasPorPack: 30,
        cantidadPacks: 25,
        color: 'negro',
        precioPorBolsaARS: 47.99,
        precioPorPackARS: 1439.73,
        precioTotalARS: 35993.16,
        costoTotalARS: 0,
        margenPct: 0,
        template: pdfTemplate
      });
      if (realPreviewUrl) window.URL.revokeObjectURL(realPreviewUrl);
      const url = window.URL.createObjectURL(blob);
      setRealPreviewUrl(url);
    } finally {
      setIsGeneratingRealPreview(false);
    }
  };



  useEffect(() => {
    const loadConfigFromDb = async () => {
      setIsLoadingConfig(true);
      try {
        const res = await apiService.getPricingConfig();
        if (res?.config) {
          const merged = normalizePricingConfig(res.config);
          setValorDolar(merged.valorDolar);
          setPreciosPorColor(merged.preciosPorColor);
          setPdfTemplate(merged.pdfTemplate);
          savePricingConfig(merged);
        }
      } catch {
        // fallback local/default
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfigFromDb();
  }, []);

  useEffect(() => {
    const autoFetchAndSave = async () => {
      try {
        setIsFetchingRate(true);
        const response = await apiService.getCurrencyRates();
        const buy = Number(response?.rates?.buy || 0);
        const sell = Number(response?.rates?.sell || 0);
        setLastRates((buy > 0 || sell > 0) ? { buy, sell } : null);
        const rate = sell > 0 ? sell : 0;
        if (response?.success && !response?.fallback && rate > 0) {
          setValorDolar(rate);
          const nextConfig = { valorDolar: rate, preciosPorColor, pdfTemplate };
          savePricingConfig(nextConfig);
          try {
            await apiService.savePricingConfig(nextConfig);
          } catch {
            // mantiene guardado local si falla DB
          }
          setMessage(`Dolar venta actualizado automatico desde ${response.source}: ${rate.toFixed(2)} ARS`);
          window.setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('No se pudo actualizar automaticamente con cotizacion real.');
          window.setTimeout(() => setMessage(''), 3000);
        }
      } catch {
        // Silencioso: mantiene el valor guardado manualmente
      } finally {
        setIsFetchingRate(false);
      }
    };

    if (!isLoadingConfig) autoFetchAndSave();
    // Solo al entrar a Configuracion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingConfig]);

  useEffect(() => {
    return () => {
      if (realPreviewUrl) window.URL.revokeObjectURL(realPreviewUrl);
    };
  }, [realPreviewUrl]);

  useEffect(() => {
    if (activeSubmodule === 'pdf' && !realPreviewUrl) {
      generateRealPdfPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubmodule]);

  useEffect(() => {
    if (activeSubmodule !== 'pdf') return;
    const timer = window.setTimeout(() => {
      generateRealPdfPreview();
    }, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfTemplate, activeSubmodule]);


  const colors: ColorKey[] = ['negro', 'blanco', 'azul', 'verde', 'rojo'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Configuracion</h1>
        <p className="text-gray-600">Fuente unica de precios para todo el panel admin</p>
      </div>

      <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setActiveSubmodule('general')}
          className={`px-3 py-2 rounded ${activeSubmodule === 'general' ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          General
        </button>
        <button
          type="button"
          onClick={() => setActiveSubmodule('pdf')}
          className={`px-3 py-2 rounded ${activeSubmodule === 'pdf' ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          PDF Presupuesto
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setActiveSubmodule('usuarios')}
            className={`px-3 py-2 rounded ${activeSubmodule === 'usuarios' ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Usuarios
          </button>
        )}
      </div>

      {activeSubmodule === 'usuarios' && isAdmin && (
        <Users />
      )}

      {activeSubmodule === 'pdf' && (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
        <h2 className="font-semibold text-gray-900">Plantilla de PDF (Presupuesto)</h2>
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Editá el presupuesto haciendo click sobre cualquier parte del diseño de abajo.
          Después ajustás texto, color y tamaño desde <span className="font-semibold">Editor rápido</span>.
        </div>

        <div className="space-y-2 rounded-lg border border-gray-200 p-4 bg-gray-50">
          <h3 className="font-medium text-gray-900">Validez del presupuesto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Etiqueta</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={pdfTemplate.validityLabel}
                onChange={(e) => setPdfTemplate((p) => ({ ...p, validityLabel: e.target.value }))}
                placeholder="Validez"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Valor</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={pdfTemplate.validityValue}
                onChange={(e) => setPdfTemplate((p) => ({ ...p, validityValue: e.target.value }))}
                placeholder="30 dias"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2 rounded-lg border border-gray-200 p-4 bg-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium text-gray-900">Lineas legales</h3>
            <button type="button" onClick={addLegalLine} className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">Agregar legal</button>
          </div>
          {pdfTemplate.legalLines.map((line, index) => (
            <div key={index} className="flex items-center gap-2">
              <input className="w-full border rounded px-3 py-2" value={line} onChange={(e) => updateLegalLine(index, e.target.value)} />
              <button type="button" onClick={() => removeLegalLine(index)} className="px-3 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50">Quitar</button>
            </div>
          ))}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div
            className={`px-4 py-3 text-white cursor-pointer ${selectedPreviewElement === 'headerBg' ? 'outline outline-2 outline-blue-400' : ''}`}
            style={{ backgroundColor: pdfTemplate.colors.headerBg }}
            onClick={() => openEditorFor('headerBg')}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="/images/logo.jpg" alt="Logo" className="w-14 h-14 object-contain bg-white p-1 rounded" />
                <div>
                  <div
                    className={`font-bold cursor-pointer ${selectedPreviewElement === 'companyName' ? 'outline outline-2 outline-blue-400' : ''}`}
                    style={{ color: pdfTemplate.colors.textOnDark, fontSize: `${pdfTemplate.fontSizes.companyName}px` }}
                    onClick={(e) => { e.stopPropagation(); openEditorFor('companyName'); }}
                  >
                    {pdfTemplate.companyName}
                  </div>
                  <div
                    className={`cursor-pointer ${selectedPreviewElement === 'companySubtitle' ? 'outline outline-2 outline-blue-400' : ''}`}
                    style={{ color: pdfTemplate.colors.accent, fontSize: `${pdfTemplate.fontSizes.companySubtitle}px` }}
                    onClick={(e) => { e.stopPropagation(); openEditorFor('companySubtitle'); }}
                  >
                    {pdfTemplate.companySubtitle}
                  </div>
                </div>
              </div>
              <div className={`text-right cursor-pointer ml-auto mr-1 self-center ${selectedPreviewElement === 'badge' ? 'outline outline-2 outline-blue-400' : ''}`} style={{ color: pdfTemplate.colors.badgeText }} onClick={(e) => { e.stopPropagation(); openEditorFor('badge'); }}>
                <div className="font-semibold" style={{ fontSize: `${pdfTemplate.fontSizes.badgeTitle}px` }}>Nº 2026-005</div>
                <div style={{ fontSize: `${pdfTemplate.fontSizes.badgeSubtitle}px` }}>{pdfTemplate.quoteTitle}</div>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-100 border-y">
            <div className="flex items-center justify-between text-sm" style={{ color: pdfTemplate.colors.textPrimary }}>
              <div className="space-x-2">
                <span className="font-semibold">Fecha:</span>
                <span>21 de abril de 2026</span>
              </div>
              <div className="space-x-2">
                <span className={`font-semibold cursor-pointer ${selectedPreviewElement === 'validity' ? 'outline outline-2 outline-blue-400' : ''}`} onClick={() => openEditorFor('validity')}>{pdfTemplate.validityLabel}:</span>
                <span className={`cursor-pointer ${selectedPreviewElement === 'validity' ? 'outline outline-2 outline-blue-400' : ''}`} onClick={() => openEditorFor('validity')}>{pdfTemplate.validityValue}</span>
              </div>
            </div>
            <div className="mt-1 text-sm space-x-2" style={{ color: pdfTemplate.colors.textPrimary }}>
              <span className="font-semibold">Cliente:</span>
              <span>Appteck</span>
            </div>
          </div>
          <div
            className={`px-4 py-3 border-b cursor-pointer ${selectedPreviewElement === 'contact' ? 'outline outline-2 outline-blue-400' : ''}`}
            style={{ backgroundColor: pdfTemplate.colors.contactBg }}
            onClick={() => openEditorFor('contact')}
          >
            <div style={{ color: pdfTemplate.colors.contactTitle, fontSize: `${pdfTemplate.fontSizes.contactTitle}px` }} className="font-semibold">
              {pdfTemplate.contactTitle}
            </div>
            <div className="mt-1" style={{ color: pdfTemplate.colors.contactText, fontSize: `${pdfTemplate.fontSizes.contactBody}px` }}>
              Tel: (264) 411-4422&nbsp;&nbsp; | &nbsp;&nbsp;Email: polcenplast@gmail.com
            </div>
            <div style={{ color: pdfTemplate.colors.contactText, fontSize: `${pdfTemplate.fontSizes.contactBody}px` }}>
              Dir: Santiago de Liniers 1239, Rivadavia, San Juan, Argentina
            </div>
          </div>
          <div className="p-4 pt-6 bg-white">
            <div
              className={`font-semibold mb-2 cursor-pointer ${selectedPreviewElement === 'sectionTitle' ? 'outline outline-2 outline-blue-400' : ''}`}
              style={{ color: pdfTemplate.colors.sectionTitle, fontSize: `${pdfTemplate.fontSizes.sectionTitle}px` }}
              onClick={() => openEditorFor('sectionTitle')}
            >
              {pdfTemplate.sectionTitle}
            </div>
            <div className="border">
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-50 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Producto</div>
                <div className="px-3 py-2 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Bolsas Negro</div>
              </div>
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Medida</div>
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>45 x 60 cm</div>
              </div>
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-50 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Micrones</div>
                <div className="px-3 py-2 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>23 um</div>
              </div>
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Cantidad</div>
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>750 bolsas (25 packs x 30 unidades)</div>
              </div>
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-50 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Precio por bolsa</div>
                <div className="px-3 py-2 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>$47,99</div>
              </div>
              <div className="grid grid-cols-2 border-b">
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textMuted, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>Precio por pack</div>
                <div className="px-3 py-2 bg-gray-100 cursor-pointer" style={{ color: pdfTemplate.colors.textPrimary, fontSize: `${pdfTemplate.fontSizes.body}px` }} onClick={() => openEditorFor('body')}>$1.439,73</div>
              </div>
              <div className="grid grid-cols-2 font-semibold cursor-pointer" style={{ backgroundColor: pdfTemplate.colors.totalBg, color: pdfTemplate.colors.textOnDark, fontSize: `${pdfTemplate.fontSizes.total}px` }} onClick={() => openEditorFor('total')}>
                <div className="px-3 py-2">TOTAL</div>
                <div className="px-3 py-2">$35.993,16 ARS</div>
              </div>
            </div>
            <div className="mt-3 border cursor-pointer" onClick={() => openEditorFor('legal')}>
              <div className="px-3 py-1 font-semibold" style={{ backgroundColor: pdfTemplate.colors.legalHeaderBg, color: pdfTemplate.colors.legalHeaderText, fontSize: `${pdfTemplate.fontSizes.legalTitle}px` }}>
                {pdfTemplate.legalTitle}
              </div>
              <div className="px-3 py-2 text-sm space-y-1" style={{ color: pdfTemplate.colors.legalText, fontSize: `${pdfTemplate.fontSizes.legal}px` }}>
                {pdfTemplate.legalLines.map((line, index) => (
                  <div key={index}>{line || `Legal ${index + 1}`}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-white space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium text-gray-900">PDF real completo</h3>
            <button
              type="button"
              onClick={generateRealPdfPreview}
              disabled={isGeneratingRealPreview}
              className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
            >
              {isGeneratingRealPreview ? 'Actualizando...' : 'Actualizar PDF real'}
            </button>
          </div>
          {realPreviewUrl ? (
            <iframe title="PDF real completo" src={realPreviewUrl} className="w-full h-[70vh] sm:h-[900px] border rounded" />
          ) : (
            <p className="text-sm text-gray-600">Cargando PDF real...</p>
          )}
        </div>

        {isEditorModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setIsEditorModalOpen(false)}>
          <div className="w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] overflow-auto border rounded-t-lg sm:rounded-lg p-4 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Editor rápido (según elemento seleccionado)</h3>
            <button type="button" className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" onClick={() => setIsEditorModalOpen(false)}>Cerrar</button>
          </div>
          {selectedPreviewElement === 'headerBg' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ColorSwatchPicker label="Color fondo encabezado" value={pdfTemplate.colors.headerBg} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, headerBg: value } }))} />
              <ColorSwatchPicker label="Color texto sobre oscuro" value={pdfTemplate.colors.textOnDark} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, textOnDark: value } }))} />
            </div>
          )}
          {selectedPreviewElement === 'companyName' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Texto</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.companyName} onChange={(e) => setPdfTemplate((p) => ({ ...p, companyName: e.target.value }))} /></div>
              <ColorSwatchPicker label="Color texto" value={pdfTemplate.colors.textOnDark} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, textOnDark: value } }))} />
              <div><label className="text-sm font-medium">Tamaño</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.companyName} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, companyName: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'companySubtitle' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Texto</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.companySubtitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, companySubtitle: e.target.value }))} /></div>
              <ColorSwatchPicker label="Color texto/acento" value={pdfTemplate.colors.accent} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, accent: value } }))} />
              <div><label className="text-sm font-medium">Tamaño</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.companySubtitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, companySubtitle: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'badge' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Texto</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.quoteTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, quoteTitle: e.target.value }))} /></div>
              <ColorSwatchPicker label="Color fondo" value={pdfTemplate.colors.badgeBg} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, badgeBg: value } }))} />
              <ColorSwatchPicker label="Color texto" value={pdfTemplate.colors.badgeText} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, badgeText: value } }))} />
              <div><label className="text-sm font-medium">Tamaño número</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.badgeTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, badgeTitle: Number(e.target.value) || 0 } }))} /></div>
              <div><label className="text-sm font-medium">Tamaño subtítulo</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.badgeSubtitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, badgeSubtitle: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'sectionTitle' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Texto</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.sectionTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, sectionTitle: e.target.value }))} /></div>
              <ColorSwatchPicker label="Color título sección" value={pdfTemplate.colors.sectionTitle} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, sectionTitle: value } }))} />
              <div><label className="text-sm font-medium">Tamaño</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.sectionTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, sectionTitle: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Título</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.contactTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, contactTitle: e.target.value }))} /></div>
              <ColorSwatchPicker label="Fondo contacto" value={pdfTemplate.colors.contactBg} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, contactBg: value } }))} />
              <ColorSwatchPicker label="Color título" value={pdfTemplate.colors.contactTitle} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, contactTitle: value } }))} />
              <ColorSwatchPicker label="Color texto" value={pdfTemplate.colors.contactText} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, contactText: value } }))} />
              <div><label className="text-sm font-medium">Tamaño título</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.contactTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, contactTitle: Number(e.target.value) || 0 } }))} /></div>
              <div><label className="text-sm font-medium">Tamaño texto</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.contactBody} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, contactBody: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'validity' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Etiqueta</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.validityLabel} onChange={(e) => setPdfTemplate((p) => ({ ...p, validityLabel: e.target.value }))} /></div>
              <div><label className="text-sm font-medium">Valor</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.validityValue} onChange={(e) => setPdfTemplate((p) => ({ ...p, validityValue: e.target.value }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'body' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ColorSwatchPicker label="Color texto principal" value={pdfTemplate.colors.textPrimary} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, textPrimary: value } }))} />
              <ColorSwatchPicker label="Color texto secundario" value={pdfTemplate.colors.textMuted} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, textMuted: value } }))} />
              <div><label className="text-sm font-medium">Tamaño</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.body} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, body: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'total' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ColorSwatchPicker label="Fondo total" value={pdfTemplate.colors.totalBg} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, totalBg: value } }))} />
              <ColorSwatchPicker label="Texto sobre oscuro" value={pdfTemplate.colors.textOnDark} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, textOnDark: value } }))} />
              <div><label className="text-sm font-medium">Tamaño</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.total} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, total: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          {selectedPreviewElement === 'legal' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-sm font-medium">Titulo legal</label><input className="w-full border rounded px-3 py-2" value={pdfTemplate.legalTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, legalTitle: e.target.value }))} /></div>
              <ColorSwatchPicker label="Fondo encabezado legal" value={pdfTemplate.colors.legalHeaderBg} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, legalHeaderBg: value } }))} />
              <ColorSwatchPicker label="Color titulo legal" value={pdfTemplate.colors.legalHeaderText} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, legalHeaderText: value } }))} />
              <div><label className="text-sm font-medium">Tamao titulo legal</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.legalTitle} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, legalTitle: Number(e.target.value) || 0 } }))} /></div>
              <ColorSwatchPicker label="Color texto legal" value={pdfTemplate.colors.legalText} onChange={(value) => setPdfTemplate((p) => ({ ...p, colors: { ...p.colors, legalText: value } }))} />
              <div><label className="text-sm font-medium">Tamao legal</label><input type="number" className="w-full border rounded px-3 py-2" value={pdfTemplate.fontSizes.legal} onChange={(e) => setPdfTemplate((p) => ({ ...p, fontSizes: { ...p.fontSizes, legal: Number(e.target.value) || 0 } }))} /></div>
            </div>
          )}
          </div>
        </div>
        )}
      </div>
      )}

      {activeSubmodule === 'general' && (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
        <h2 className="font-semibold text-gray-900">Precios por color (USD/kg)</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {colors.map((k) => (
            <div key={k}>
              <label className="text-sm font-medium capitalize">{k}</label>
              <input
                className="w-full border rounded px-3 py-2"
                type="number"
                step="0.01"
                value={preciosPorColor[k]}
                onChange={(e) => setPreciosPorColor((p) => ({ ...p, [k]: Number(e.target.value) }))}
              />
            </div>
          ))}
        </div>

        <div className="max-w-sm">
          <label className="text-sm font-medium">Valor dolar (ARS)</label>
          <input
            className="w-full border rounded px-3 py-2"
            type="number"
            step="1"
            min={1}
            value={valorDolar}
            onChange={(e) => setValorDolar(Number(e.target.value))}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onFetchDollarRate}
            disabled={isFetchingRate}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
          >
            {isFetchingRate ? 'Actualizando...' : 'Traer dolar actual'}
          </button>
        </div>

        {lastRates && (
          <div className="text-sm text-gray-700 bg-gray-50 border rounded px-3 py-2">
            Compra: ${lastRates.buy.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Venta: ${lastRates.sell.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        )}

      </div>
      )}

      {activeSubmodule !== 'usuarios' && message && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{message}</p>
      )}

      {activeSubmodule !== 'usuarios' && (
      <div>
        <button
          type="button"
          onClick={onSave}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Guardar configuracion
        </button>
      </div>
      )}

    </div>
  );
};

export default Settings;


